import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { graphql } from '@/gql';
import { GetTitleParcelAtQuery } from '@/gql/graphql';
import projectKeys from './keyFactory';

const GetTitleParcelAt = graphql(`
  query GetTitleParcelAt($projectId: ID!, $location: SolarLatLngInput) {
    titleParcelAt(projectId: $projectId, location: $location) {
      inspireId
      ring {
        lat
        lng
      }
      areaMeters2
      attribution
    }
  }
`);

export type TitleParcel = NonNullable<GetTitleParcelAtQuery['titleParcelAt']>;

/**
 * The HM Land Registry INSPIRE title parcel containing a point — a local,
 * cheap lookup (null when no parcel data covers the point). Omitting
 * `location` resolves the parcel at the project's site address point, which is
 * how the wrong-building guard gets its reference parcel: comparing the
 * ADDRESS parcel against the parcel under a dragged pin catches an analysis
 * about to run on a neighbour's roof (on semi-detached pairs an 8m geocoding
 * error crosses the party wall).
 *
 * Wherever the parcel ring is rendered its `attribution` must be displayed.
 */
const useGetTitleParcelAt = (
  projectId?: string,
  location?: { lat: number; lng: number } | null,
  options?: { enabled?: boolean }
) => {
  const { data, ...rest } = useQuery({
    enabled: !!projectId && (options?.enabled ?? true),
    queryKey: projectKeys.titleParcelAt(projectId!, location),
    queryFn: () =>
      gqlClient.request(GetTitleParcelAt, {
        projectId: projectId!,
        location: location ?? undefined,
      }),
    // Title parcels are static registry data — cache generously per point.
    staleTime: 1000 * 60 * 30,
  });
  return {
    parcel: data?.titleParcelAt ?? null,
    ...rest,
  };
};

export default useGetTitleParcelAt;
